/**
 * Shortcuts Hooks
 *
 * Extracted hooks to avoid circular dependencies with lazy loading
 */

import { useState, useCallback, useEffect } from 'react';
import { useKeyboardShortcuts } from '@/providers/KeyboardShortcutsProvider';

export interface ShortcutsHelpState {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  toggle: () => void;
}

export function useShortcutsHelp(): ShortcutsHelpState {
  const [isOpen, setIsOpen] = useState(false);
  const { preferences } = useKeyboardShortcuts();

  const show = useCallback(() => setIsOpen(true), []);
  const hide = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    if (!preferences.helpDisplay.enableHelpOverlay) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Show help on ? key (but not in input fields)
      if (event.key === '?' && !event.shiftKey) {
        const target = event.target as HTMLElement;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.contentEditable === 'true')
        ) {
          return;
        }

        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [preferences.helpDisplay.enableHelpOverlay]);

  return {
    isOpen,
    show,
    hide,
    toggle,
  };
}
